import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Edit2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic CRUD page component.
 * fields: [{ key, label, type: 'text'|'number'|'email'|'date'|'textarea'|'select', options?, placeholder? }]
 * columns: [{ key, label, render?(row) }]
 */
export default function CrudPage({ title, endpoint, fields, columns, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const r = await api.get(endpoint);
    setRows(r.data);
    const editId = new URLSearchParams(window.location.search).get("edit");
    const row = editId && (r.data || []).find(x => x.id === editId);
    if (row) { openEdit(row); window.history.replaceState(null, "", window.location.pathname); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => {
    const empty = {};
    fields.forEach(f => { empty[f.key] = f.default ?? (f.type === "number" ? 0 : ""); });
    setForm(empty); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => {
    const f = {};
    fields.forEach(fd => { if (fd.type !== "custom") f[fd.key] = row[fd.key] ?? (fd.type === "number" ? 0 : ""); });
    setForm(f); setEditing(row); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach(f => { if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0; });
      if (editing) await api.patch(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Salvato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!confirm("Eliminare?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCSV = () => {
    const header = columns.map(c => c.label).join(";");
    const lines = [header];
    rows.forEach(r => {
      lines.push(columns.map(c => {
        const v = c.exportValue ? c.exportValue(r) : r[c.key];
        return (v ?? "").toString().replace(/;/g, ",");
      }).join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase()}.csv`;
    a.click();
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    return JSON.stringify(r).toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{title}</h1>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mt-1">{rows.length} elementi</div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Input placeholder="Cerca..." value={q} onChange={e=>setQ(e.target.value)} className="bg-slate-950 border-slate-800 w-full sm:w-56"/>
          <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900" onClick={exportCSV}><Download size={16}/> CSV</Button>
          <Button data-testid={`${testidPrefix}-new-btn`} onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Plus size={16} strokeWidth={3}/> Nuovo</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                {columns.map(c => <th key={c.key} className="text-left p-3">{c.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filtered.length === 0 && <tr><td colSpan={columns.length + 1} className="p-10 text-center text-slate-500">Nessun elemento</td></tr>}
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-slate-900/40">
                  {columns.map(c => (
                    <td key={c.key} className="p-3 text-slate-300">
                      {c.render ? c.render(r) : (r[c.key] ?? "-")}
                    </td>
                  ))}
                  <td className="pr-3">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`${testidPrefix}-edit-${r.id}`} onClick={()=>openEdit(r)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400"><Edit2 size={14}/></button>
                      <button data-testid={`${testidPrefix}-delete-${r.id}`} onClick={()=>remove(r.id)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{editing ? `Modifica ${title}` : `Nuovo ${title}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {fields.filter(f => !(f.createOnly && editing) && !(f.type === "custom" && !editing)).map(f => (
              <div key={f.key} className={f.fullWidth ? "md:col-span-2" : ""}>
                <Label className="text-xs text-slate-500 mb-1 block">{f.label}</Label>
                {f.type === "custom" ? (
                  f.render(editing)
                ) : f.type === "textarea" ? (
                  <Textarea rows={2} value={form[f.key] || ""} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="bg-slate-900 border-slate-800"/>
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={v=>setForm({...form,[f.key]:v})}>
                    <SelectTrigger className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      {(f.options || []).map(o => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`${testidPrefix}-field-${f.key}`} type={f.type || "text"} value={form[f.key] ?? ""} placeholder={f.placeholder} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="bg-slate-900 border-slate-800"/>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setOpen(false)}>Annulla</Button>
            <Button data-testid={`${testidPrefix}-save-btn`} onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">{saving?"Salvataggio...":"Salva"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
